import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material'
// import GridViewIcon from '@mui/icons-material/GridView' // Commented out - nested layout disabled
import axios from 'axios'

import {
  DashboardCell,
  DashboardTextData,
  DashboardConfig,
  ImageWidgetData,
  VideoWidgetData,
  TableGraphWidgetData,
  AgentArtifactWidgetData,
  TitleHeroWidgetData,
  TOCWidgetData,
  KPICardWidgetData,
  CalloutWidgetData,
  MultiTableConfigTable,
  MultiTable,
  BrandTheme, ChartTitleDesign } from '../../types/gotypes'
import { API_BASE_URL } from '../../constants/system'
import { RichTextEditor } from './widgets/RichTextWidget'
// import { renderLayoutWidgetEditor } from './widgets/LayoutWidget' // Commented out - nested layout disabled
import { MultiTableAnalysisWidgetEditor } from './widgets/MultiTableAnalysisWidget'
import { GenAIWizard, GenAIWizardCompleteResult } from './widgets/genai/GenAIWizard'
import { GuidedToggleState, applyGuidedToggles, deriveGuidedToggles } from './widgets/genai/wizardHelpers'
import { ImageWidgetEditor } from './widgets/ImageWidget'
import { VideoWidgetEditor } from './widgets/VideoWidget'
import { TableGraphWidgetEditor } from './widgets/TableGraphWidget'
import { AgentArtifactWidgetEditor } from './widgets/AgentArtifactWidget'
import { TitleHeroWidgetEditor } from './widgets/TitleHeroWidget'
import { KPICardWidgetEditor } from './widgets/KPICardWidget'
import { CalloutWidgetEditor } from './widgets/CalloutWidget'
import { TOCWidgetEditor } from './widgets/TOCWidget'
import ReportPillarEditor from './widgets/ReportPillarEditor'

// Maps a saved cell's widget_type to the DashboardCell field that holds its
// data. 'table' and 'chart' (sidebar-only pseudo-types) both resolve to
// 'table_graph' before this is consulted; 'agent_artifacts' doesn't follow the
// `${widget_type}_data` naming convention, so this is an explicit table
// rather than a derived string (#1069/#1071 data-loss fix).
const WIDGET_TYPE_TO_DATA_KEY: Record<string, keyof DashboardCell> = {
  text: 'text_data',
  layout: 'layout_data',
  multi_table_analysis: 'multi_table_analysis_data',
  image: 'image_data',
  video: 'video_data',
  table_graph: 'table_graph_data',
  agent_artifacts: 'agent_artifact_data',
  title_hero: 'title_hero_data',
  toc: 'toc_data',
  kpi_card: 'kpi_card_data',
  callout: 'callout_data',
  report_pillar: 'report_pillar_data',
}

// Switching widget type in the modal must not destroy the *_data blocks of
// widget types the user is merely passing through (#1069/#1071: image ->
// video -> image was wiping the image). Those blocks are kept on `editedCell`
// for the life of the modal session; this strips every block except the
// active widget's immediately before the cell is handed to onSave, so only
// the saved type's data actually persists.
const sanitizeCellForSave = (cell: DashboardCell): DashboardCell => {
  const activeKey = WIDGET_TYPE_TO_DATA_KEY[cell.widget_type]
  const cleaned: DashboardCell = { ...cell }
  const dataKeys = new Set(Object.values(WIDGET_TYPE_TO_DATA_KEY))
  dataKeys.forEach((key) => {
    if (key !== activeKey) {
      delete cleaned[key]
    }
  })
  return cleaned
}

interface CellEditModalProps {
  open: boolean
  cell: DashboardCell | null
  customer: string
  job: string
  dashboardId: string
  isManager?: boolean
  dashboardConfig?: DashboardConfig
  currentPageIndex?: number
  // Brand themes (#1193/#1175/#1171) — the chart editor's theme selector and
  // its Preview's colours. Supplied by DashboardEditor (page → hook →
  // component); optional so other callers render unchanged.
  themes?: BrandTheme[]
  pageThemeName?: string
  colorMap?: Record<string, string>
  themeColorMaps?: Record<string, Record<string, string>>
  /** #1810: the page's filter map, sent with a new Gen AI card's preview run. */
  jobFilters?: Record<string, string>
  onSave: (cell: DashboardCell) => void
  onClose: () => void
}

/** Undefined for `{}` (nothing selected) rather than persisting an empty
 * object — keeps `analysis_guided_toggles` absent for widgets that never
 * touched the toggles, same as every other optional field on this data. */
function emptyToggleStateToUndefined(toggles: GuidedToggleState): GuidedToggleState | undefined {
  return (toggles.format || toggles.length || toggles.audience) ? toggles : undefined
}

const CellEditModal: React.FC<CellEditModalProps> = ({
  open,
  cell,
  customer,
  job,
  dashboardId,
  isManager,
  dashboardConfig,
  currentPageIndex = 0,
  themes,
  pageThemeName,
  colorMap,
  themeColorMaps,
  jobFilters,
  onSave,
  onClose,
}) => {
  const [editedCell, setEditedCell] = useState<DashboardCell | null>(null)
  // Tables a card added inside the pillar editor still needs created (#1080).
  // Keyed by entry index, handed to the save path the same way the Gen AI card
  // editor hands over its own.
  const [pillarPendingTables, setPillarPendingTables] = useState<Record<number, MultiTableConfigTable[]>>({})

  // State for multi-table analysis editor
  const [selectedTables, setSelectedTables] = useState<MultiTableConfigTable[]>([])
  // #1282: true when this card's multi-table definition could not be loaded
  // (a copy made before the fork fix points at a row this dashboard doesn't
  // own). Tell the user rather than showing a blank picker.
  const [definitionMissing, setDefinitionMissing] = useState(false)
  const [widgetTitle, setWidgetTitle] = useState<string>('')
  // #1247 item 1: title formatting, the chart widgets' ChartTitleDesign.
  const [widgetTitleDesign, setWidgetTitleDesign] = useState<ChartTitleDesign | undefined>(undefined)
  const [userContext, setUserContext] = useState<string>('')
  const [synthesisUserContext, setSynthesisUserContext] = useState<string>('')
  const [analysisInstructions, setAnalysisInstructions] = useState<string>('')
  const [synthesisInstructions, setSynthesisInstructions] = useState<string>('')
  // Structured guided-toggle state (#1047) — seeded alongside the instructions
  // text below so reopening this modal re-derives the toggle UI instead of
  // always showing defaults.
  const [analysisGuidedToggles, setAnalysisGuidedToggles] = useState<GuidedToggleState>({})
  const [synthesisGuidedToggles, setSynthesisGuidedToggles] = useState<GuidedToggleState>({})

  // Reset form when modal opens with new cell
  React.useEffect(() => {
    if (open && cell) {
      setEditedCell({ ...cell })
      setPillarPendingTables({})

      // Initialize multi-table state if editing existing multi-table analysis
      if (cell.widget_type === 'multi_table_analysis' && cell.multi_table_analysis_data) {
        setWidgetTitle(cell.multi_table_analysis_data.title || '')
        setWidgetTitleDesign(cell.multi_table_analysis_data.title_design)
        setUserContext(cell.multi_table_analysis_data.user_context || '')
        setSynthesisUserContext(cell.multi_table_analysis_data.synthesis_user_context || '')
        setAnalysisInstructions(cell.multi_table_analysis_data.analysis_instructions || '')
        setSynthesisInstructions(cell.multi_table_analysis_data.synthesis_instructions || '')
        setAnalysisGuidedToggles(deriveGuidedToggles(
          cell.multi_table_analysis_data.analysis_instructions || '',
          cell.multi_table_analysis_data.analysis_guided_toggles,
        ))
        setSynthesisGuidedToggles(deriveGuidedToggles(
          cell.multi_table_analysis_data.synthesis_instructions || '',
          cell.multi_table_analysis_data.synthesis_guided_toggles,
        ))

        // Load existing tables from the multi-table record
        const multiTableId = cell.multi_table_analysis_data.multi_table_id
        if (multiTableId && customer && dashboardId) {
          setDefinitionMissing(false)
          axios.get<MultiTable>(
            `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/multitables/${multiTableId}`
          ).then(res => {
            const tables = res.data?.config?.tables
            if (tables && tables.length > 0) {
              setSelectedTables(tables)
            }
          }).catch(err => {
            console.error('Failed to load multi-table tables:', err)
            // #1282: a 404 means the card points at a definition this
            // dashboard does not own — flag it so the picker explains itself.
            if (axios.isAxiosError(err) && err.response?.status === 404) {
              setSelectedTables([])
              setDefinitionMissing(true)
            }
          })
        } else {
          setSelectedTables([])
          setDefinitionMissing(false)
        }
        setPillarPendingTables({})
      } else {
        setPillarPendingTables({})
        setSelectedTables([])
        setDefinitionMissing(false)
        setWidgetTitle('')
        setWidgetTitleDesign(undefined)
        setUserContext('')
        setSynthesisUserContext('')
        setAnalysisInstructions('')
        setSynthesisInstructions('')
        setAnalysisGuidedToggles({})
        setSynthesisGuidedToggles({})
      }
    }
  }, [open, cell, customer, dashboardId])

  if (!editedCell) return null

  const handleTextDataChange = (textData: DashboardTextData) => {
    setEditedCell({
      ...editedCell,
      text_data: textData,
    })
  }

  const handleSave = () => {
    if (!editedCell) return

    if (editedCell.widget_type === 'report_pillar') {
      onSave({
        ...sanitizeCellForSave(editedCell),
        // @ts-expect-error - temporary property for the save path to create the
        // multi-tables of cards added inside the pillar editor (#1080)
        _pillarPendingTables: pillarPendingTables,
      })
      return
    }

    if (editedCell.widget_type === 'multi_table_analysis') {
      // The format/length/audience a user picks on the Table Analysis tab
      // describes the text they expect to SEE — which is the SYNTHESIS
      // output. When they've expressed nothing for the synthesis stage (no
      // toggles, no instructions text), the analysis-side choice flows
      // through; anything they set on the synthesis tab wins over this.
      // Without this, "Flowing prose" on the analysis tab regenerated into
      // bullets, because the synthesis stage never heard the request
      // (Adam, 2026-08-25 — the third surface after the wizard and prompt
      // priority fixes in #1125).
      const synthesisSaysNothing = !synthesisInstructions &&
        !(synthesisGuidedToggles.format || synthesisGuidedToggles.length || synthesisGuidedToggles.audience)
      const inheritedToggles = emptyToggleStateToUndefined(analysisGuidedToggles)
      const inheritSynthesis = synthesisSaysNothing && inheritedToggles !== undefined
      const updatedCell: DashboardCell = {
        ...editedCell,
        multi_table_analysis_data: {
          ...editedCell.multi_table_analysis_data,
          multi_table_id: editedCell.multi_table_analysis_data?.multi_table_id || '',
          job: job,
          title: widgetTitle || undefined,
          title_design: widgetTitleDesign,
          user_context: userContext || undefined,
          synthesis_user_context: synthesisUserContext || undefined,
          analysis_instructions: analysisInstructions || undefined,
          synthesis_instructions: (inheritSynthesis
            ? applyGuidedToggles('', analysisGuidedToggles)
            : synthesisInstructions) || undefined,
          analysis_guided_toggles: emptyToggleStateToUndefined(analysisGuidedToggles),
          synthesis_guided_toggles: inheritSynthesis
            ? inheritedToggles
            : emptyToggleStateToUndefined(synthesisGuidedToggles),
        },
      }

      onSave({
        ...sanitizeCellForSave(updatedCell),
        // @ts-expect-error - temporary property for parent to create multi-table
        _pendingTables: selectedTables,
      })
    } else {
      onSave(sanitizeCellForSave(editedCell))
    }
  }

  const canSave = () => {
    if (editedCell.widget_type === 'multi_table_analysis') {
      return selectedTables.length > 0 || Boolean(editedCell.multi_table_analysis_data?.multi_table_id)
    }
    return true
  }

  // First-time creation of a Gen AI Insights card gets the 4-step wizard
  // (#970 Part A); once `multi_table_id` is set, the ⚙️ cog reopens this same
  // modal but with the flat editor below instead (Part B) — restructuring the
  // existing editor layout minimally rather than re-running the wizard.
  const isNewGenAIInsight = editedCell.widget_type === 'multi_table_analysis' && !editedCell.multi_table_analysis_data?.multi_table_id

  // Builds the final cell directly from the wizard's own state rather than
  // going through `selectedTables`/`widgetTitle`/etc — those are only ever
  // populated by the reset effect above for an EXISTING card, and reading them
  // here would race the wizard's just-applied setState calls (same class of
  // stale-closure bug `useDashboardCellSave` guards against elsewhere).
  const handleWizardComplete = (result: GenAIWizardCompleteResult) => {
    if (!editedCell) return
    const updatedCell: DashboardCell = {
      ...editedCell,
      widget_type: 'multi_table_analysis',
      multi_table_analysis_data: {
        ...editedCell.multi_table_analysis_data,
        // The wizard may have already created a live multi-table while
        // generating its preview — carry that id forward so the save doesn't
        // create (and auto-run) a second one.
        multi_table_id: result.multiTableId || editedCell.multi_table_analysis_data?.multi_table_id || '',
        job,
        title: result.title || undefined,
        analysis_instructions: result.instructions || undefined,
        analysis_guided_toggles: emptyToggleStateToUndefined(result.guidedToggles),
        // The wizard's format/length/audience toggles describe the text the
        // user will SEE — which for a multi-table card is the SYNTHESIS
        // output, not the per-table analyses. Mirror the guidance sentence
        // into the synthesis instructions too (idempotent via the guidance
        // marker), or a "flowing prose" choice never reaches the stage that
        // writes the visible report (#1117 follow-up, Adam 2026-08-25).
        synthesis_instructions:
          applyGuidedToggles(editedCell.multi_table_analysis_data?.synthesis_instructions || '', result.guidedToggles) || undefined,
        synthesis_guided_toggles: emptyToggleStateToUndefined(result.guidedToggles),
        persona: editedCell.multi_table_analysis_data?.persona || 'Executive Summarizer',
      },
    }
    onSave({
      ...sanitizeCellForSave(updatedCell),
      // @ts-expect-error - temporary property for parent to create/update the multi-table
      _pendingTables: result.tables,
    })
  }

  // #1075 belt-and-braces: `layout` cells are column containers with no editor
  // and no entry in the widget taxonomy. If this modal is ever opened on one,
  // it must not be able to swap the column for a single empty widget — so
  // Save is withheld and the modal only explains itself.
  const isLayoutCell = editedCell.widget_type === 'layout'

  // #1080: a report pillar gets its OWN editor. The #1075 destruction class
  // stays unrepresentable.
  const isReportPillar = editedCell.widget_type === 'report_pillar'

  // The modal's ONE heading (#1132): "Configure <widget>", top-left. The
  // widget-type sidebar and the "Edit Cell" title are gone — to change a
  // widget's type, delete it and add another (Matt: "this is not that
  // area"). The per-type *_data blocks preserved for type switching
  // (#1069/#1071) are moot without the switcher; sanitizeCellForSave stays
  // as a harmless guard for legacy cells still carrying stale blocks.
  const configureTitle = (() => {
    switch (editedCell.widget_type) {
      case 'text': return 'Configure Text Box'
      case 'multi_table_analysis': return 'Configure Gen AI Insights'
      case 'table_graph':
        return editedCell.table_graph_data?.view_mode === 'graph' ? 'Configure Chart' : 'Configure Table'
      case 'image': return 'Configure Image'
      case 'video': return 'Configure Video'
      case 'agent_artifacts': return 'Configure Agent Artifacts'
      case 'title_hero': return 'Configure Title'
      case 'toc': return 'Configure Table of Contents'
      case 'kpi_card': return 'Configure KPI Card'
      case 'callout': return 'Configure Callout'
      case 'spacer': return 'Configure Spacer'
      default: return 'Configure Widget'
    }
  })()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: '95vw', height: '90vh', maxWidth: 1600 }
      }}
    >
      {(isLayoutCell || isReportPillar) && (
        <DialogTitle>{isLayoutCell ? 'Column' : 'Report Pillar'}</DialogTitle>
      )}
      <DialogContent sx={{ display: 'flex', gap: 2, p: 0, overflow: 'hidden' }}>
        {isLayoutCell && (
          <Box sx={{ flex: 1, p: 3 }}>
            <Alert severity="info">
              This is a column container; edit the widgets inside it.
            </Alert>
          </Box>
        )}

        {/* The pillar's own editor. It replaces the widget-type sidebar
            rather than sitting beside it — see ReportPillarEditor's header. */}
        {isReportPillar && (
          <Box sx={{ flex: 1, p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <ReportPillarEditor
              data={editedCell.report_pillar_data || { heading: '', entries: [] }}
              customer={customer}
              job={job}
              dashboardId={dashboardId}
              jobFilters={jobFilters}
              onChange={(d) => setEditedCell({ ...editedCell, report_pillar_data: d })}
              onPendingTablesChange={(entryIndex, tables) =>
                setPillarPendingTables((prev) => ({ ...prev, [entryIndex]: tables }))
              }
            />
          </Box>
        )}

        {/* Widget editor — full width (#1132): the widget-type sidebar is
            gone from every editor, so the "Configure <widget>" heading below
            is the modal's top-left anchor. */}
        {!isLayoutCell && !isReportPillar && (
        <Box sx={{ flex: 1, p: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {configureTitle}
          </Typography>
          {/* No job warning for multi-table analysis */}
          {editedCell.widget_type === 'multi_table_analysis' && !job && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please open a job in your session to use multi-table analysis.
              Go to Tables, select a customer and job, then return here.
            </Alert>
          )}

          {/* #1187: a spacer has no settings — its editor only explains itself. */}
          {editedCell.widget_type === 'spacer' && (
            <Alert severity="info">
              A spacer is empty space. It has nothing to configure — place it
              between widgets for a horizontal gap, or in its own row for
              vertical breathing room, then resize or delete it like any other
              widget. It is invisible on the finished dashboard.
            </Alert>
          )}

          {editedCell.widget_type === 'text' && (
            <Box sx={{ flex: 1 }}>
              <RichTextEditor
                textData={editedCell.text_data}
                onChange={handleTextDataChange}
              />
            </Box>
          )}

          {editedCell.widget_type === 'multi_table_analysis' && job && isNewGenAIInsight && (
            <GenAIWizard
              customer={customer}
              job={job}
              dashboardId={dashboardId}
              jobFilters={jobFilters}
              onComplete={handleWizardComplete}
              onCancel={onClose}
            />
          )}

          {editedCell.widget_type === 'multi_table_analysis' && job && !isNewGenAIInsight && definitionMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This card&apos;s analysis definition is missing — choose its tables
              again and Save to rebuild it. (This happens on a dashboard copied
              before the copy fix; re-picking repairs it.)
            </Alert>
          )}

          {editedCell.widget_type === 'multi_table_analysis' && job && !isNewGenAIInsight && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1 }}>
                <MultiTableAnalysisWidgetEditor
                  customer={customer}
                  job={job}
                  data={editedCell.multi_table_analysis_data}
                  tables={selectedTables}
                  title={widgetTitle}
                  userContext={userContext}
                  synthesisUserContext={synthesisUserContext}
                  analysisInstructions={analysisInstructions}
                  synthesisInstructions={synthesisInstructions}
                  analysisGuidedToggles={analysisGuidedToggles}
                  synthesisGuidedToggles={synthesisGuidedToggles}
                  onTablesChange={setSelectedTables}
                  onTitleChange={setWidgetTitle}
                  titleDesign={widgetTitleDesign}
                  onTitleDesignChange={setWidgetTitleDesign}
                  onUserContextChange={setUserContext}
                  onSynthesisUserContextChange={setSynthesisUserContext}
                  onAnalysisInstructionsChange={setAnalysisInstructions}
                  onSynthesisInstructionsChange={setSynthesisInstructions}
                  onAnalysisGuidedTogglesChange={setAnalysisGuidedToggles}
                  onSynthesisGuidedTogglesChange={setSynthesisGuidedToggles}
                />
              </Box>
            </Box>
          )}

          {editedCell.widget_type === 'image' && (
            <Box sx={{ flex: 1 }}>
              <ImageWidgetEditor
                customer={customer}
                job={job}
                dashboardId={dashboardId}
                data={editedCell.image_data}
                onDataChange={(imageData: ImageWidgetData) => {
                  setEditedCell({ ...editedCell, image_data: imageData })
                }}
              />
            </Box>
          )}

          {editedCell.widget_type === 'video' && (
            <Box sx={{ flex: 1 }}>
              <VideoWidgetEditor
                customer={customer}
                job={job}
                dashboardId={dashboardId}
                data={editedCell.video_data}
                onDataChange={(videoData: VideoWidgetData) => {
                  setEditedCell({ ...editedCell, video_data: videoData })
                }}
              />
            </Box>
          )}

          {editedCell.widget_type === 'table_graph' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* The "Configure Chart"/"Configure Table" heading is GONE
                  (#1176 Surface 3, Matt's instruction) — the sidebar already
                  identifies the widget type (the #969 Obs 12 comment that
                  fixed its wording said as much), and the row cost the tree
                  a line of height. */}
              <Box sx={{ flex: 1 }}>
                <TableGraphWidgetEditor
                  customer={customer}
                  job={job}
                  dashboardId={dashboardId}
                  dashboardName={dashboardConfig?.name || ''}
                  data={editedCell.table_graph_data}
                  themes={themes}
                  pageThemeName={pageThemeName}
                  colorMap={colorMap}
                  themeColorMaps={themeColorMaps}
                  onDataChange={(tgData: TableGraphWidgetData) => {
                    setEditedCell({ ...editedCell, table_graph_data: tgData })
                  }}
                />
              </Box>
            </Box>
          )}

          {editedCell.widget_type === 'agent_artifacts' && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1 }}>
                <AgentArtifactWidgetEditor
                  customer={customer}
                  data={editedCell.agent_artifact_data}
                  onDataChange={(artifactData: AgentArtifactWidgetData) => {
                    setEditedCell({ ...editedCell, agent_artifact_data: artifactData })
                  }}
                />
              </Box>
            </Box>
          )}

          {editedCell.widget_type === 'title_hero' && (
            <Box sx={{ flex: 1 }}>
              <TitleHeroWidgetEditor
                data={editedCell.title_hero_data}
                onDataChange={(d: TitleHeroWidgetData) => {
                  setEditedCell({ ...editedCell, title_hero_data: d })
                }}
              />
            </Box>
          )}

          {editedCell.widget_type === 'toc' && (
            <Box sx={{ flex: 1 }}>
              <TOCWidgetEditor
                data={editedCell.toc_data}
                onDataChange={(d: TOCWidgetData) => {
                  setEditedCell({ ...editedCell, toc_data: d })
                }}
                dashboardConfig={dashboardConfig}
                currentPageIndex={currentPageIndex}
              />
            </Box>
          )}

          {editedCell.widget_type === 'kpi_card' && (
            <Box sx={{ flex: 1 }}>
              <KPICardWidgetEditor
                data={editedCell.kpi_card_data}
                onDataChange={(d: KPICardWidgetData) => {
                  setEditedCell({ ...editedCell, kpi_card_data: d })
                }}
              />
            </Box>
          )}

          {editedCell.widget_type === 'callout' && (
            <Box sx={{ flex: 1 }}>
              <CalloutWidgetEditor
                data={editedCell.callout_data}
                onDataChange={(d: CalloutWidgetData) => {
                  setEditedCell({ ...editedCell, callout_data: d })
                }}
              />
            </Box>
          )}
        </Box>
        )}
      </DialogContent>
      {isLayoutCell && (
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={onClose} variant="contained" color="info">Close</Button>
        </DialogActions>
      )}
      {/* The wizard drives its own save (Step 3 "Accept + Save") — its Cancel
          link and this dialog's own Cancel button both close without saving,
          so the generic footer only needs to disappear, not duplicate it. */}
      {(!isLayoutCell && !isNewGenAIInsight) && (
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="info"
            disabled={!canSave()}
          >
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default CellEditModal
